import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  NgZone,
  HostListener
} from '@angular/core';
import * as THREE from 'three';

const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = `
  precision highp float;
  uniform float uTime;
  uniform vec3 uResolution;
  uniform vec3 uBaseColor;
  uniform float uAmplitude;
  uniform float uFrequencyX;
  uniform float uFrequencyY;
  uniform vec2 uMouse;
  varying vec2 vUv;

  vec4 renderImage(vec2 uvCoord) {
      vec2 fragCoord = uvCoord * uResolution.xy;
      vec2 uv = (2.0 * fragCoord - uResolution.xy) / min(uResolution.x, uResolution.y);

      for (float i = 1.0; i < 10.0; i++){
          uv.x += uAmplitude / i * cos(i * uFrequencyX * uv.y + uTime + uMouse.x * 3.14159);
          uv.y += uAmplitude / i * cos(i * uFrequencyY * uv.x + uTime + uMouse.y * 3.14159);
      }

      vec2 diff = (uvCoord - uMouse);
      float dist = length(diff);
      float falloff = exp(-dist * 20.0);
      float ripple = sin(10.0 * dist - uTime * 2.0) * 0.03;
      uv += (diff / (dist + 0.0001)) * ripple * falloff;

      vec3 color = uBaseColor / abs(sin(uTime - uv.y - uv.x));
      return vec4(color, 1.0);
  }

  void main() {
      vec4 col = vec4(0.0);
      int samples = 0;
      for (int i = -1; i <= 1; i++){
          for (int j = -1; j <= 1; j++){
              vec2 offset = vec2(float(i), float(j)) * (1.0 / min(uResolution.x, uResolution.y));
              col += renderImage(vUv + offset);
              samples++;
          }
      }
      gl_FragColor = col / float(samples);
  }
`;

@Component({
  selector: 'nc-liquid-chrome-background',
  templateUrl: './liquid-chrome-background.component.html',
  styleUrls: ['./liquid-chrome-background.component.less']
})
export class LiquidChromeBackgroundComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;

  /** 基础颜色数组 [r, g, b]（0-1） */
  @Input() ncBaseColor: number[] = [0.1, 0.1, 0.1];
  /** 动画运行速度（0-5） */
  @Input() ncSpeed: number = 0.2;
  /** 波动幅度（0.1-1） */
  @Input() ncAmplitude: number = 0.3;
  /** X轴波频 */
  @Input() ncFrequencyX: number = 3;
  /** Y轴波频 */
  @Input() ncFrequencyY: number = 3;
  /** 是否允许鼠标/触屏交互 */
  private _interactive: boolean = true;
  @Input() set ncInteractive(value: boolean | string) {
    this._interactive = value !== null && value !== undefined && value !== false && value !== 'false';
  }
  get ncInteractive(): boolean {
    return this._interactive;
  }

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.OrthographicCamera;
  private material!: THREE.ShaderMaterial;
  private mesh!: THREE.Mesh;

  private animationId: number | null = null;
  private resizeObserver!: ResizeObserver;
  private mouse = new THREE.Vector2(0, 0);

  constructor(private ngZone: NgZone) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    this.initWebGL();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.material) return;

    if (changes['ncBaseColor'] && changes['ncBaseColor'].currentValue) {
      this.material.uniforms['uBaseColor'].value.fromArray(this.ncBaseColor);
    }
    if (changes['ncAmplitude'] !== undefined) {
      this.material.uniforms['uAmplitude'].value = this.ncAmplitude;
    }
    if (changes['ncFrequencyX'] !== undefined) {
      this.material.uniforms['uFrequencyX'].value = this.ncFrequencyX;
    }
    if (changes['ncFrequencyY'] !== undefined) {
      this.material.uniforms['uFrequencyY'].value = this.ncFrequencyY;
    }
    // ncInteractive handled via HostListener check
  }

  private initWebGL(): void {
    const container = this.containerRef.nativeElement;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    // 原版 jsx 清除颜色为白色
    this.renderer.setClearColor(0xffffff, 1);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();

    // 正交相机渲染全屏 Plane
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    this.camera.position.z = 1;

    const geometry = new THREE.PlaneGeometry(2, 2);

    this.material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uResolution: { value: new THREE.Vector3() },
        uBaseColor: { value: new THREE.Vector3().fromArray(this.ncBaseColor) },
        uAmplitude: { value: this.ncAmplitude },
        uFrequencyX: { value: this.ncFrequencyX },
        uFrequencyY: { value: this.ncFrequencyY },
        uMouse: { value: this.mouse }
      }
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.scene.add(this.mesh);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);

    this.resize();

    // Interaction handled via HostListener

    // 提升性能，不让 requestAnimationFrame 触发 Angular 变更检测
    this.ngZone.runOutsideAngular(() => {
      this.update(0);
    });
  }

  private resize(): void {
    if (!this.containerRef || !this.renderer) return;
    const container = this.containerRef.nativeElement;
    const width = container.offsetWidth;
    const height = container.offsetHeight;

    this.renderer.setSize(width, height);
    this.material.uniforms['uResolution'].value.set(width, height, width / height);
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.ncInteractive || !this.containerRef) return;
    const container = this.containerRef.nativeElement;
    const rect = container.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = 1 - (event.clientY - rect.top) / rect.height;

    this.mouse.set(x, y);
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.ncInteractive || !this.containerRef) return;
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      const container = this.containerRef.nativeElement;
      const rect = container.getBoundingClientRect();
      const x = (touch.clientX - rect.left) / rect.width;
      const y = 1 - (touch.clientY - rect.top) / rect.height;
      this.mouse.set(x, y);
    }
  }



  private update = (t: number) => {
    this.animationId = requestAnimationFrame(this.update);

    if (this.material) {
      this.material.uniforms['uTime'].value = t * 0.001 * this.ncSpeed;
    }

    this.renderer.render(this.scene, this.camera);
  };

  private cleanup(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }



    if (this.mesh) {
      this.mesh.geometry.dispose();
      (this.mesh.material as THREE.Material).dispose();
      this.scene.remove(this.mesh);
    }

    if (this.renderer) {
      this.renderer.dispose();
      const dom = this.renderer.domElement;
      if (dom && dom.parentElement) {
        dom.parentElement.removeChild(dom);
      }
      this.renderer.getContext()?.getExtension('WEBGL_lose_context')?.loseContext();
    }
  }
}

